import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Zap, Building2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { PolarEmbedCheckout } from "@polar-sh/checkout/embed";
import { useSession } from "@/hooks/use-session";
import { signInWithGoogle } from "@/lib/auth";

interface PricingModalProps {
  open: boolean;
  onClose: () => void;
  currentPlan?: string;
}

export function PricingModal({ open, onClose, currentPlan }: PricingModalProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const { session } = useSession();

  const handleUpgrade = async (plan: "pro" | "enterprise") => {
    if (!session) {
      void signInWithGoogle();
      return;
    }

    setLoading(plan);
    try {
      const res = await fetch(`/api/user/checkout?plan=${plan}`, {
        method: "POST",
      });
      
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? "Failed to create checkout");
      }
      
      const { checkout_url } = (await res.json()) as { checkout_url: string };
      
      const isDark = document.documentElement.classList.contains("dark");
      await PolarEmbedCheckout.create(checkout_url, { theme: isDark ? "dark" : "light" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Checkout failed",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setLoading(null);
    }
  };

  const tiers = [
    {
      name: "Free",
      id: "free",
      price: "$0",
      description: "Perfect for testing the waters",
      features: [
        "2 generations total",
        "Public gallery only",
        "Standard SVG export",
      ],
      buttonText: "Current Plan",
      disabled: true,
      icon: <Zap className="w-5 h-5 text-muted-foreground" />,
    },
    {
      name: "Pro",
      id: "pro",
      price: "$19",
      description: "For active creators",
      features: [
        "20 generations per month",
        "Free SVG downloads",
        "Priority generation",
      ],
      buttonText: "Upgrade to Pro",
      icon: <Sparkles className="w-5 h-5 text-primary" />,
      popular: true,
    },
    {
      name: "Enterprise",
      id: "enterprise",
      price: "$99",
      description: "For professional brand work",
      features: [
        "200 generations per month",
        "Free SVG downloads",
        "Custom Brand Style guidelines",
        "Instant Restyling tool",
      ],
      buttonText: "Go Enterprise",
      icon: <Building2 className="w-5 h-5 text-primary" />,
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] glass">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Upgrade your creativity</DialogTitle>
          <DialogDescription className="text-center">
            Choose the plan that fits your workflow.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6">
          {tiers.map((tier) => (
            <div 
              key={tier.id} 
              className="flex flex-col rounded-xl border p-4 space-y-4 border-border bg-card"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold">{tier.name}</h3>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">{tier.price}</span>
                  <span className="text-xs text-muted-foreground">/mo</span>
                </div>
                <p className="text-[11px] text-muted-foreground">{tier.description}</p>
              </div>

              <ul className="space-y-2 flex-1">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-xs">
                    <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={tier.id === "pro" ? "default" : "outline"}
                className="w-full h-9 text-xs"
                disabled={tier.disabled || (currentPlan === tier.id) || !!loading}
                onClick={() => handleUpgrade(tier.id as "pro" | "enterprise")}
              >
                {currentPlan === tier.id ? "Active" : tier.buttonText}
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter className="sm:justify-center">
          <p className="text-[10px] text-muted-foreground">
            Payments are securely handled by Polar. Cancel anytime.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
